import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, TextField, Button, IconButton, Grid, Chip,
    Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SellIcon from '@mui/icons-material/PointOfSale';
import InventoryIcon from '@mui/icons-material/Inventory2';
import HistoryIcon from '@mui/icons-material/History';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { inventoryAPI } from '../services/api';
import { mobileCardTableSx } from '../styles/mobileCardTable';
import { toast } from 'react-toastify';
import { darkSelectMenuProps } from '../styles/selectMenuStyles';

const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: logoColors.brightYellow,
        '& fieldset': { borderColor: `${logoColors.lightYellow}50` },
        '&:hover fieldset': { borderColor: logoColors.lightYellow },
        '&.Mui-focused fieldset': { borderColor: logoColors.brightYellow },
    },
    '& .MuiInputLabel-root': {
        color: `${logoColors.brightYellow}90`,
        '&.Mui-focused': { color: logoColors.brightYellow },
    },
};

const emptyForm = { name: '', stock: '', costPrice: '', sellingPrice: '' };

// label/colors for the Payment chip in sales history - keyed by the paymentMethod string
// the backend stores (null/anything else falls back to Cash, matching legacy rows).
const PAYMENT_STYLES = {
    CASH: { label: 'Cash', bg: '#4caf5025', color: '#4caf50' },
    ONLINE: { label: 'Online', bg: '#2196f325', color: '#2196f3' },
    CREDIT: { label: 'Credit', bg: `${logoColors.lightYellow}25`, color: logoColors.lightYellow },
};
const paymentStyle = (method) => PAYMENT_STYLES[method] || PAYMENT_STYLES.CASH;

const InventoryPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // Sell flow: pick an item + qty, add it to a cart, repeat for other items, then check out
    // the whole cart under one buyer name - this is what lets one sale cover Lays + Pepsi etc.
    const [sellItemId, setSellItemId] = useState('');
    const [sellQuantity, setSellQuantity] = useState(1);
    const [buyerName, setBuyerName] = useState('');
    const [cart, setCart] = useState([]); // [{ itemId, name, quantity, sellingPrice, stock }]
    const [paymentMethod, setPaymentMethod] = useState('CASH'); // 'CASH' | 'CREDIT'
    const [amountPaidNow, setAmountPaidNow] = useState(''); // only used when paymentMethod is CREDIT
    const [selling, setSelling] = useState(false);

    const [sales, setSales] = useState([]);
    const [salesLoading, setSalesLoading] = useState(true);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await inventoryAPI.getItems();
            setItems(res.data);
        } catch (error) {
            toast.error('Failed to load inventory');
        } finally {
            setLoading(false);
        }
    };

    const fetchSales = async () => {
        setSalesLoading(true);
        try {
            const res = await inventoryAPI.getRecentSales();
            setSales(res.data);
        } catch (error) {
            toast.error('Failed to load sales history');
        } finally {
            setSalesLoading(false);
        }
    };

    useEffect(() => { fetchItems(); fetchSales(); }, []);

    // Simple running totals from the sales log - no separate "reports" page needed for this.
    const summary = useMemo(() => sales.reduce((acc, s) => ({
        itemsSold: acc.itemsSold + s.quantity,
        revenue: acc.revenue + s.revenue,
        profit: acc.profit + s.profit,
    }), { itemsSold: 0, revenue: 0, profit: 0 }), [sales]);

    const selectedSellItem = useMemo(
        () => items.find(i => i.id === sellItemId),
        [items, sellItemId]
    );

    const openAddForm = () => {
        setEditingItem(null);
        setForm(emptyForm);
        setFormOpen(true);
    };

    const openEditForm = (item) => {
        setEditingItem(item);
        setForm({
            name: item.name,
            stock: item.stock,
            costPrice: item.costPrice,
            sellingPrice: item.sellingPrice,
        });
        setFormOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error('Item name is required');
            return;
        }
        const payload = {
            name: form.name.trim(),
            stock: parseInt(form.stock, 10) || 0,
            costPrice: parseFloat(form.costPrice) || 0,
            sellingPrice: parseFloat(form.sellingPrice) || 0,
        };
        setSaving(true);
        try {
            if (editingItem) {
                await inventoryAPI.updateItem(editingItem.id, payload);
                toast.success('Item updated');
            } else {
                await inventoryAPI.addItem(payload);
                toast.success('Item added');
            }
            setFormOpen(false);
            fetchItems();
        } catch (error) {
            toast.error('Failed to save item: ' + (error.response?.data?.message || error.message));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Remove "${item.name}" from inventory?`)) return;
        try {
            await inventoryAPI.deleteItem(item.id);
            toast.success('Item removed');
            fetchItems();
        } catch (error) {
            toast.error('Failed to remove item');
        }
    };

    // Undoes a sale (test or mistaken) without needing DB access: removes the history row and
    // adds the quantity back to that item's stock, if the item still exists.
    const handleDeleteSale = async (sale) => {
        if (!window.confirm(`Delete this sale (${sale.quantity} × ${sale.itemName})? Stock will be added back.`)) return;
        try {
            await inventoryAPI.deleteSale(sale.id);
            toast.success('Sale deleted, stock restored');
            fetchItems();
            fetchSales();
        } catch (error) {
            toast.error('Failed to delete sale');
        }
    };

    const cartTotal = useMemo(
        () => cart.reduce((sum, line) => sum + line.sellingPrice * line.quantity, 0),
        [cart]
    );

    const addToCart = () => {
        if (!sellItemId) {
            toast.error('Pick an item first');
            return;
        }
        const item = selectedSellItem;
        const alreadyInCart = cart.find(l => l.itemId === sellItemId)?.quantity || 0;
        if (alreadyInCart + sellQuantity > item.stock) {
            toast.error(`Only ${item.stock} of ${item.name} in stock (${alreadyInCart} already in cart)`);
            return;
        }
        setCart(prev => {
            const existing = prev.find(l => l.itemId === sellItemId);
            if (existing) {
                return prev.map(l => l.itemId === sellItemId ? { ...l, quantity: l.quantity + sellQuantity } : l);
            }
            return [...prev, { itemId: item.id, name: item.name, quantity: sellQuantity, sellingPrice: item.sellingPrice, stock: item.stock }];
        });
        setSellItemId('');
        setSellQuantity(1);
    };

    const removeFromCart = (itemId) => {
        setCart(prev => prev.filter(l => l.itemId !== itemId));
    };

    // +/- on an already-added cart line. Removes the line entirely once it hits 0, and can't
    // go past however much stock that item actually has.
    const adjustCartQuantity = (itemId, delta) => {
        setCart(prev => {
            const next = prev.map(l => {
                if (l.itemId !== itemId) return l;
                const quantity = Math.min(Math.max(l.quantity + delta, 0), l.stock);
                return { ...l, quantity };
            });
            return next.filter(l => l.quantity > 0);
        });
    };

    const paidAmountNumber = parseFloat(amountPaidNow) || 0;
    const creditDue = Math.max(cartTotal - paidAmountNumber, 0);

    const completeSale = async () => {
        if (cart.length === 0) {
            toast.error('Add at least one item to the cart');
            return;
        }
        if (!buyerName.trim()) {
            toast.error('Buyer name is required');
            return;
        }
        setSelling(true);
        const name = buyerName.trim();
        let soldCount = 0;
        try {
            // Sequential, not parallel: each sale checks live stock server-side, so selling
            // two cart lines for the same item at once could both pass a stale stock check.
            for (const line of cart) {
                const lineRevenue = line.sellingPrice * line.quantity;
                // Split whatever was actually paid across cart lines proportionally to each
                // line's share of the total, so each history row's own "due" makes sense too.
                const linePaid = paymentMethod === 'CREDIT'
                    ? Math.round((paidAmountNumber * (lineRevenue / cartTotal)) * 100) / 100
                    : undefined;
                await inventoryAPI.sellItem(line.itemId, line.quantity, name, paymentMethod, linePaid);
                soldCount++;
            }
            toast.success(`Sold ${cart.length} item${cart.length > 1 ? 's' : ''} to ${name}`);
            setCart([]);
            setBuyerName('');
            setPaymentMethod('CASH');
            setAmountPaidNow('');
        } catch (error) {
            toast.error(
                `Stopped after ${soldCount}/${cart.length} items - ` +
                (error.response?.data?.message || error.message)
            );
            setCart(prev => prev.slice(soldCount));
        } finally {
            setSelling(false);
            fetchItems();
            fetchSales();
        }
    };

    return (
        <Box>
            {/* Sell - the action used most, so it's front and center */}
            <Paper
                elevation={4}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    mb: 3,
                    borderRadius: '18px',
                    background: `linear-gradient(135deg, ${logoColors.primaryBlue} 0%, ${logoColors.darkBlue} 100%)`,
                    border: `1px solid ${logoColors.lightYellow}40`,
                }}
            >
                <Typography sx={{ color: logoColors.brightYellow, fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SellIcon /> Sell items
                </Typography>

                {/* Row 1: pick an item + qty, add it to the cart - repeat for each different product */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                    <FormControl size="small" sx={{ ...fieldSx, flex: '1 1 220px', minWidth: 180 }}>
                        <InputLabel>Item</InputLabel>
                        <Select
                            value={sellItemId}
                            label="Item"
                            onChange={(e) => setSellItemId(e.target.value)}
                            sx={{ color: logoColors.brightYellow }}
                            MenuProps={darkSelectMenuProps(logoColors)}
                        >
                            {items.map(item => (
                                <MenuItem key={item.id} value={item.id} disabled={item.stock <= 0}>
                                    {item.name} {item.stock <= 0 ? '(out of stock)' : `(${item.stock} left)`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ color: `${logoColors.brightYellow}90`, fontSize: '0.8rem', mr: 0.5 }}>Qty</Typography>
                        <IconButton
                            size="small"
                            disabled={sellQuantity <= 1}
                            onClick={() => setSellQuantity(q => Math.max(q - 1, 1))}
                            sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}40` }}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700, minWidth: 24, textAlign: 'center' }}>
                            {sellQuantity}
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={!!selectedSellItem && sellQuantity >= selectedSellItem.stock}
                            onClick={() => setSellQuantity(q => Math.min(q + 1, selectedSellItem?.stock || q + 1))}
                            sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}40` }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        disabled={!sellItemId}
                        onClick={addToCart}
                        sx={{ borderColor: logoColors.lightYellow, color: logoColors.lightYellow, borderRadius: '10px', '&:hover': { borderColor: logoColors.brightYellow, color: logoColors.brightYellow } }}
                    >
                        Add to cart
                    </Button>
                </Box>

                {/* Row 2: the cart - one line per distinct item, all sold together under one buyer */}
                {cart.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, borderRadius: '12px', background: 'rgba(255,255,255,0.05)' }}>
                        {cart.map(line => (
                            <Box key={line.itemId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
                                <ShoppingCartIcon sx={{ fontSize: '1rem', color: `${logoColors.brightYellow}70` }} />
                                <Typography sx={{ color: logoColors.brightYellow, flex: 1, minWidth: 100 }}>
                                    {line.name}
                                </Typography>
                                <IconButton size="small" onClick={() => adjustCartQuantity(line.itemId, -1)} sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}30` }}>
                                    <RemoveIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                                <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                                    {line.quantity}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={line.quantity >= line.stock}
                                    onClick={() => adjustCartQuantity(line.itemId, 1)}
                                    sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}30` }}
                                >
                                    <AddIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                                <Typography sx={{ color: logoColors.lightYellow, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                                    Rs {(line.sellingPrice * line.quantity).toLocaleString()}
                                </Typography>
                                <IconButton size="small" onClick={() => removeFromCart(line.itemId)} sx={{ color: `${logoColors.brightYellow}70` }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Divider sx={{ borderColor: `${logoColors.lightYellow}20`, my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ color: logoColors.brightYellow, fontWeight: 700 }}>Total</Typography>
                            <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700 }}>Rs {cartTotal.toLocaleString()}</Typography>
                        </Box>
                    </Box>
                )}

                {/* Row 3: buyer name, cash/credit, and checkout - only shown once there's something to sell */}
                {cart.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                label="Buyer name"
                                required
                                value={buyerName}
                                onChange={(e) => setBuyerName(e.target.value)}
                                sx={{ ...fieldSx, flex: '1 1 200px', minWidth: 160 }}
                            />
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={paymentMethod}
                                onChange={(_, val) => val && setPaymentMethod(val)}
                                sx={{
                                    borderRadius: '10px', overflow: 'hidden', border: `1px solid ${logoColors.lightYellow}40`,
                                    '& .MuiToggleButton-root': {
                                        color: `${logoColors.brightYellow}90`, border: 'none', px: 2, textTransform: 'none', gap: 0.75,
                                        '&.Mui-selected': {
                                            background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                                            color: logoColors.darkBlue,
                                            '&:hover': { background: logoColors.lightYellow },
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="CASH"><PaymentsIcon fontSize="small" /> Cash</ToggleButton>
                                <ToggleButton value="ONLINE"><AccountBalanceIcon fontSize="small" /> Online</ToggleButton>
                                <ToggleButton value="CREDIT"><CreditCardIcon fontSize="small" /> Credit</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {paymentMethod === 'CREDIT' && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 1.5 }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Paid now (Rs)"
                                    value={amountPaidNow}
                                    onChange={(e) => setAmountPaidNow(e.target.value)}
                                    placeholder="0"
                                    inputProps={{ min: 0, max: cartTotal }}
                                    sx={{ ...fieldSx, width: 150 }}
                                />
                                <Chip
                                    size="small"
                                    label={creditDue > 0 ? `Rs ${creditDue.toLocaleString()} on credit` : 'Fully paid'}
                                    sx={{
                                        fontWeight: 600,
                                        bgcolor: creditDue > 0 ? '#ff980025' : '#4caf5025',
                                        color: creditDue > 0 ? '#ff9800' : '#4caf50',
                                    }}
                                />
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            disabled={selling || !buyerName.trim()}
                            onClick={completeSale}
                            sx={{
                                mt: 2,
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                                color: logoColors.darkBlue, fontWeight: 700, borderRadius: '10px', px: 3,
                            }}
                        >
                            {selling ? 'Selling...' : `Complete sale (Rs ${cartTotal.toLocaleString()})`}
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Running totals from the sales log - the "reports" for this, kept to one line */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                {[
                    { label: 'Items sold', value: summary.itemsSold },
                    { label: 'Revenue', value: `Rs ${summary.revenue.toLocaleString()}` },
                    { label: 'Profit', value: `Rs ${summary.profit.toLocaleString()}` },
                ].map(stat => (
                    <Paper key={stat.label} sx={{
                        flex: '1 1 150px', p: 2, borderRadius: '14px', textAlign: 'center',
                        background: 'rgba(255,255,255,0.03)', border: `1px solid ${logoColors.lightYellow}30`,
                    }}>
                        <Typography sx={{ color: `${logoColors.brightYellow}80`, fontSize: '0.8rem' }}>{stat.label}</Typography>
                        <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700, fontSize: '1.3rem' }}>{stat.value}</Typography>
                    </Paper>
                ))}
            </Box>

            {/* Stock list */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ color: logoColors.brightYellow, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon /> Stock
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={openAddForm}
                    variant="outlined"
                    size="small"
                    sx={{ borderColor: logoColors.lightYellow, color: logoColors.lightYellow, '&:hover': { borderColor: logoColors.brightYellow, color: logoColors.brightYellow } }}
                >
                    Add item
                </Button>
            </Box>

            {loading ? (
                <Typography sx={{ color: `${logoColors.brightYellow}80`, textAlign: 'center', py: 4 }}>Loading...</Typography>
            ) : items.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${logoColors.lightYellow}30` }}>
                    <Typography sx={{ color: `${logoColors.brightYellow}90` }}>
                        Nothing in stock yet. Add your first item to get started.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {items.map(item => {
                        const margin = item.sellingPrice - item.costPrice;
                        const lowStock = item.stock <= 5;
                        return (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <Paper
                                    sx={{
                                        p: 2.5, borderRadius: '16px', height: '100%',
                                        background: 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${logoColors.lightYellow}30`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography sx={{ color: logoColors.brightYellow, fontWeight: 700, fontSize: '1.05rem' }}>
                                            {item.name}
                                        </Typography>
                                        <Box>
                                            <IconButton size="small" onClick={() => openEditForm(item)} sx={{ color: logoColors.lightYellow }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(item)} sx={{ color: '#ff9800' }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={`${item.stock} in stock`}
                                        sx={{
                                            mb: 1.5, fontWeight: 600,
                                            bgcolor: lowStock ? '#ff980025' : '#4caf5025',
                                            color: lowStock ? '#ff9800' : '#4caf50',
                                        }}
                                    />
                                    <Divider sx={{ borderColor: `${logoColors.lightYellow}20`, mb: 1.5 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                        <Typography sx={{ color: `${logoColors.brightYellow}80` }}>Cost</Typography>
                                        <Typography sx={{ color: logoColors.brightYellow }}>Rs {item.costPrice}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                        <Typography sx={{ color: `${logoColors.brightYellow}80` }}>Sell price</Typography>
                                        <Typography sx={{ color: logoColors.brightYellow }}>Rs {item.sellingPrice}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', mt: 0.5 }}>
                                        <Typography sx={{ color: `${logoColors.brightYellow}80` }}>Margin</Typography>
                                        <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700 }}>Rs {margin}</Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Sales history */}
            <Box sx={{ mt: 4, mb: 2 }}>
                <Typography sx={{ color: logoColors.brightYellow, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon /> Sales history
                </Typography>
            </Box>
            {salesLoading ? (
                <Typography sx={{ color: `${logoColors.brightYellow}80`, textAlign: 'center', py: 2 }}>Loading...</Typography>
            ) : sales.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '16px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${logoColors.lightYellow}30` }}>
                    <Typography sx={{ color: `${logoColors.brightYellow}90` }}>No sales yet.</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: '16px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${logoColors.lightYellow}30`, overflowX: 'auto' }}>
                    <Table size="small" sx={mobileCardTableSx(logoColors.lightYellow, `${logoColors.lightYellow}30`)}>
                        <TableHead>
                            <TableRow>
                                {['Item', 'Buyer', 'Qty', 'Revenue', 'Profit', 'Payment', 'When', ''].map(h => (
                                    <TableCell key={h} sx={{ color: logoColors.brightYellow, fontWeight: 700, borderBottom: `2px solid ${logoColors.lightYellow}40` }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sales.map(sale => (
                                <TableRow key={sale.id}>
                                    <TableCell data-label="Item" sx={{ color: logoColors.brightYellow, borderBottom: `1px solid ${logoColors.lightYellow}15` }}>{sale.itemName}</TableCell>
                                    <TableCell data-label="Buyer" sx={{ color: logoColors.brightYellow, borderBottom: `1px solid ${logoColors.lightYellow}15` }}>{sale.buyerName || '-'}</TableCell>
                                    <TableCell data-label="Qty" sx={{ color: logoColors.brightYellow, borderBottom: `1px solid ${logoColors.lightYellow}15` }}>{sale.quantity}</TableCell>
                                    <TableCell data-label="Revenue" sx={{ color: logoColors.brightYellow, borderBottom: `1px solid ${logoColors.lightYellow}15` }}>Rs {sale.revenue.toLocaleString()}</TableCell>
                                    <TableCell data-label="Profit" sx={{ color: logoColors.lightYellow, fontWeight: 600, borderBottom: `1px solid ${logoColors.lightYellow}15` }}>Rs {sale.profit.toLocaleString()}</TableCell>
                                    <TableCell data-label="Payment" sx={{ borderBottom: `1px solid ${logoColors.lightYellow}15` }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={paymentStyle(sale.paymentMethod).label}
                                                sx={{
                                                    fontWeight: 600, height: 22, fontSize: '0.7rem', width: 'fit-content',
                                                    bgcolor: paymentStyle(sale.paymentMethod).bg,
                                                    color: paymentStyle(sale.paymentMethod).color,
                                                }}
                                            />
                                            {sale.amountDue > 0 && (
                                                <Typography sx={{ color: '#ff9800', fontSize: '0.7rem', fontWeight: 600 }}>
                                                    Due Rs {sale.amountDue.toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell data-label="When" sx={{ color: `${logoColors.brightYellow}80`, fontSize: '0.8rem', borderBottom: `1px solid ${logoColors.lightYellow}15` }}>
                                        {new Date(sale.soldAt).toLocaleString()}
                                    </TableCell>
                                    <TableCell data-label="Delete" sx={{ borderBottom: `1px solid ${logoColors.lightYellow}15` }}>
                                        <IconButton size="small" onClick={() => handleDeleteSale(sale)} sx={{ color: '#ff9800' }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '16px', background: logoColors.darkBlue, border: `1px solid ${logoColors.lightYellow}40` } }}
            >
                <DialogTitle sx={{ color: logoColors.brightYellow, fontWeight: 700 }}>
                    {editingItem ? 'Edit item' : 'Add item'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Item name" value={form.name} required
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        autoFocus fullWidth sx={fieldSx}
                    />
                    <TextField
                        label="Stock" type="number" value={form.stock}
                        onChange={(e) => setForm({ ...form, stock: e.target.value })}
                        fullWidth sx={fieldSx}
                    />
                    <TextField
                        label="Cost price (Rs)" type="number" value={form.costPrice}
                        onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
                        fullWidth sx={fieldSx}
                    />
                    <TextField
                        label="Selling price (Rs)" type="number" value={form.sellingPrice}
                        onChange={(e) => setForm({ ...form, sellingPrice: e.target.value })}
                        fullWidth sx={fieldSx}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ color: logoColors.brightYellow }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={handleSave}
                        sx={{
                            background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                            color: logoColors.darkBlue, fontWeight: 700,
                        }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventoryPage;
